import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, ChevronRight, BookOpen } from "lucide-react";

import { caseStudies } from "@/data/caseStudies";
import PageHero from "@/components/shared/PageHero";
import Section from "@/components/shared/Section";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";

const CaseStudies = () => {
  return (
    <div className="min-h-screen text-[var(--text-primary)]">
      <PageHero
        eyebrow="Case Studies"
        eyebrowIcon={BookOpen}
        title={
          <>
            Real results, <span className="gradient-text">measurable impact.</span>
          </>
        }
        description="Detailed success stories that show how we solve real problems and deliver measurable business outcomes."
        actions={
          <>
            <Button asChild className="btn-primary rounded-full px-6 h-11">
              <Link to="/contact" className="inline-flex items-center gap-2">
                Start your project <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-outline rounded-full px-6 h-11">
              <Link to="/our-work">See our work</Link>
            </Button>
          </>
        }
      />

      <Section>
        <div className="space-y-7 max-w-6xl mx-auto">
          {caseStudies.map((c, i) => (
            <Reveal key={c.slug} variant="up" delay={(i % 3) * 0.05}>
              <article>
                <GlowCard className="p-0 overflow-hidden h-full">
                  <div className="grid lg:grid-cols-5">
                    <div className="relative lg:col-span-2 h-64 lg:h-auto lg:min-h-[320px]">
                      <img
                        src={c.image}
                        alt={c.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                      <div className="absolute inset-0 p-6 flex flex-col justify-end">
                        <p className="text-xs uppercase tracking-[0.2em] text-white/80 mb-1">Case Study</p>
                        <h3 className="text-2xl font-display font-bold text-white mb-1">{c.title}</h3>
                        <p className="text-sm font-medium text-cyan-300">{c.result}</p>
                      </div>
                    </div>

                    <div className="lg:col-span-3 p-7 sm:p-8 flex flex-col">
                      <p className="text-[var(--text-secondary)] text-sm mb-6 leading-relaxed">{c.summary}</p>

                      <div className="grid sm:grid-cols-2 gap-5 mb-6">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-[var(--brand-primary-light)] mb-1.5">
                            Problem
                          </p>
                          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{c.problemStatement}</p>
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-[var(--brand-primary-light)] mb-1.5">
                            Solution
                          </p>
                          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{c.solutionApproach}</p>
                        </div>
                      </div>

                      <div className="mb-5">
                        <p className="text-xs font-bold uppercase tracking-wider text-[var(--brand-primary-light)] mb-2">
                          Top Results
                        </p>
                        <ul className="space-y-1.5">
                          {c.results.slice(0, 2).map((item) => (
                            <li
                              key={item}
                              className="text-sm text-[var(--text-secondary)] inline-flex items-start gap-2 w-full"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-accent-cyan)] mt-2 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mb-5">
                        <p className="text-xs font-bold uppercase tracking-wider text-[var(--brand-primary-light)] mb-2">
                          Tech Stack
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {c.toolsTech.slice(0, 5).map((tool) => (
                            <span
                              key={tool}
                              className="text-xs font-medium text-[var(--text-secondary)] border border-[color:var(--border-default)] bg-[color:var(--surface-1)] rounded-full px-3 py-1"
                            >
                              {tool}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mt-auto pt-2 flex justify-end">
                        <Button asChild className="btn-primary rounded-full px-5 h-10">
                          <Link to={`/case-studies/${c.slug}`} className="inline-flex items-center gap-2 text-sm">
                            View full study <ChevronRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </GlowCard>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default CaseStudies;
